/**
 * Sites the agent published from this Work task
 * (<name>.sites.gabusercontent.com): link, expiry, and Open / Copy /
 * Unpublish actions. State comes from the task's site events; unpublishing
 * calls the control plane directly.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { View, StyleSheet, Pressable, Platform, Linking, ActivityIndicator } from 'react-native';
import PropTypes from 'prop-types';
import * as Clipboard from 'expo-clipboard';
import { useTheme } from '@/context/ThemeContext';
import { useCoworkerStore, useToastStore } from '@/store';
import { useModalHelper } from '@/hooks/useModalHelper';
import { sitesApi } from '@/lib/api';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { effectiveSiteStatus, publishedSitesFromEvents, siteExpiryLabel } from '@/lib/publishedSites';

function SiteAction({ icon, label, onPress, busy, colors }) {
  return (
    <Pressable
      onPress={onPress}
      disabled={busy}
      accessibilityRole="button"
      accessibilityLabel={label}
      hitSlop={Platform.OS === 'web' ? undefined : 8}
      style={({ hovered, pressed }) => [styles.action, { borderColor: colors.border_color_secondary }, (hovered || pressed) && { opacity: 0.7 }]}
    >
      {busy ? <ActivityIndicator size="small" color={colors.text_color_secondary} style={styles.spinner} />
        : <Icon name={icon} size={12} color={colors.text_color_secondary} />}
      <Text size="xs" weight="medium">{label}</Text>
    </Pressable>
  );
}

export function PublishedSiteCard({ site, onUnpublished }) {
  const { colors } = useTheme();
  const { addToast } = useToastStore((s) => s.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(null);
  const current = local || site;
  const status = effectiveSiteStatus(current);
  const live = status === 'live';
  const host = current.url.replace(/^https?:\/\//, '').replace(/\/$/, '');

  const open = useCallback(() => {
    if (Platform.OS === 'web') window.open(current.url, '_blank', 'noopener,noreferrer');
    else Linking.openURL(current.url).catch(() => {});
  }, [current.url]);

  const copy = useCallback(async () => {
    try {
      if (Platform.OS === 'web' && navigator?.clipboard) await navigator.clipboard.writeText(current.url);
      else await Clipboard.setStringAsync(current.url);
      addToast('Link copied', 'success');
    } catch {
      addToast('Could not copy the link', 'error');
    }
  }, [addToast, current.url]);

  const unpublish = useCallback(() => {
    showConfirmationModal({
      title: `Unpublish ${current.name}?`,
      message: 'The link stops working and its files are deleted. You can publish again with the same name for a while.',
      confirmText: 'Unpublish',
      isDangerous: true,
      onConfirm: async () => {
        setBusy(true);
        try {
          const out = await sitesApi.unpublish(current.name);
          setLocal({ ...current, ...(out?.site || {}), status: 'unpublished' });
          onUnpublished?.(current.name);
          addToast(`${current.name} was unpublished`, 'success');
        } catch (error) {
          addToast(error?.message || 'Could not unpublish the site', 'error');
        } finally {
          setBusy(false);
        }
      },
    });
  }, [addToast, current, onUnpublished, showConfirmationModal]);

  return (
    <View style={[styles.card, { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary }]} accessibilityLabel={`Published site ${host}`}>
      <View style={styles.header}>
        <View style={[styles.iconWrap, { backgroundColor: colors.solid_color_secondary }]}>
          <Icon name="globe" size={14} color={colors.text_color_primary} />
        </View>
        <View style={styles.meta}>
          <Text size="sm" weight="semibold" numberOfLines={1} selectable>{host}</Text>
          <Text size="xs" color="secondary" numberOfLines={1}>{current.title ? `${current.title} · ` : ''}{siteExpiryLabel(current)}</Text>
        </View>
      </View>
      {live ? (
        <View style={styles.actions}>
          <SiteAction icon="arrow-up-right-from-square" label="Open" onPress={open} colors={colors} />
          <SiteAction icon="copy" label="Copy link" onPress={copy} colors={colors} />
          <SiteAction icon="trash" label="Unpublish" onPress={unpublish} busy={busy} colors={colors} />
        </View>
      ) : null}
    </View>
  );
}

PublishedSiteCard.propTypes = {
  site: PropTypes.shape({ name: PropTypes.string.isRequired, url: PropTypes.string.isRequired }).isRequired,
  onUnpublished: PropTypes.func,
};

export default function WorkPublishedSites({ taskId }) {
  const events = useCoworkerStore((s) => (taskId ? s.eventsByTask[taskId] : null) || null);
  const sites = useMemo(() => publishedSitesFromEvents(events), [events]);
  if (!sites.length) return null;
  return (
    <View style={styles.list}>
      {sites.map((site) => <PublishedSiteCard key={site.name} site={site} />)}
    </View>
  );
}

WorkPublishedSites.propTypes = { taskId: PropTypes.string };

const styles = StyleSheet.create({
  list: { gap: 8, marginTop: 8 },
  card: { borderWidth: 1, borderRadius: 14, padding: 12, gap: 10, maxWidth: 520 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  iconWrap: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  meta: { flex: 1, minWidth: 0 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  action: {
    flexDirection: 'row', alignItems: 'center', gap: 6, height: 28, paddingHorizontal: 12, borderRadius: 14, borderWidth: 1,
    ...Platform.select({ web: { cursor: 'pointer' } }),
  },
  spinner: { transform: [{ scale: 0.6 }] },
});
